<template>
  <aside class="flex h-full min-h-[360px] flex-col rounded-lg border border-slate-200 bg-white lg:min-h-[640px]">
    <header class="border-b border-slate-200 px-4 py-4">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <h2 class="text-base font-semibold text-slate-900">月度方案</h2>
          <p class="mt-1 text-xs leading-5 text-slate-500">按月份维护客户名单和开票链路</p>
        </div>
        <button
          type="button"
          class="btn-primary inline-flex shrink-0 items-center gap-1.5"
          v-permission="'enterprise-invoice:plan:create'"
          @click="emit('create')"
        >
          <PlusIcon class="h-4 w-4" />
          新建
        </button>
      </div>

      <div class="mt-4 space-y-3">
        <input
          v-model="filters.keyword"
          type="search"
          class="form-search-input form-control-sm w-full"
          placeholder="搜索方案名称"
          @keyup.enter="loadPlans"
        >
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]">
          <input
            v-model="filters.planMonth"
            type="month"
            class="form-input form-control-sm w-full"
          >
          <button type="button" class="btn-secondary" @click="resetFilters">重置</button>
          <button type="button" class="btn-primary" @click="loadPlans">搜索</button>
        </div>
      </div>
    </header>

    <div class="min-h-0 flex-1 overflow-y-auto p-3">
      <div v-if="loading" class="flex h-40 items-center justify-center text-sm text-slate-500">
        加载中...
      </div>
      <div v-else-if="plans.length === 0" class="flex h-40 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500">
        暂无方案
      </div>
      <div v-else class="space-y-2">
        <button
          v-for="plan in plans"
          :key="plan.id"
          type="button"
          :class="[
            'w-full rounded-lg border p-3 text-left transition-colors',
            isSelected(plan) ? 'border-primary-300 bg-primary-50' : 'border-slate-200 bg-white hover:border-primary-200 hover:bg-slate-50'
          ]"
          @click="selectPlan(plan)"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-slate-500">{{ plan.planMonth || '-' }}</span>
                <span :class="['status-badge', statusClass(plan.status)]">{{ statusLabel(plan.status) }}</span>
              </div>
              <h3 class="mt-2 truncate text-sm font-semibold text-slate-900" :title="plan.planName">
                {{ plan.planName || '-' }}
              </h3>
            </div>
            <button
              type="button"
              class="table-action-button is-purple shrink-0"
              v-permission="'enterprise-invoice:plan:copy'"
              title="复制方案"
              @click.stop="emit('copy', plan)"
            >
              <DocumentDuplicateIcon class="h-4 w-4" />
            </button>
          </div>
          <p class="mt-2 line-clamp-2 min-h-[32px] text-xs leading-4 text-slate-500" :title="plan.lastChangeReason">
            {{ plan.lastChangeReason || '最后变更原因：-' }}
          </p>
        </button>
      </div>
    </div>
  </aside>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { DocumentDuplicateIcon, PlusIcon } from '@heroicons/vue/24/outline'
import { getEnterpriseInvoicePlanPage } from '@/api/invoice'
import { statusLabel, statusTone } from '../utils'

const props = defineProps({
  modelValue: {
    type: [Number, String],
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'create', 'copy'])

const loading = ref(false)
const plans = ref([])
const filters = reactive({
  keyword: '',
  planMonth: ''
})
let loadRequestSeq = 0

function statusClass(status) {
  const tone = statusTone(status)
  const toneClasses = {
    neutral: 'bg-slate-100 text-slate-600',
    primary: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    red: 'bg-red-100 text-red-700'
  }
  return toneClasses[tone] || toneClasses.neutral
}

function isSelected(plan) {
  return String(plan?.id) === String(props.modelValue)
}

function selectPlan(plan) {
  emit('update:modelValue', plan?.id || null)
}

function resetFilters() {
  filters.keyword = ''
  filters.planMonth = ''
  loadPlans()
}

async function loadPlans() {
  const requestSeq = ++loadRequestSeq
  loading.value = true
  try {
    const result = await getEnterpriseInvoicePlanPage({
      keyword: filters.keyword?.trim(),
      planMonth: filters.planMonth || undefined,
      page: 1,
      size: 50
    })
    if (requestSeq !== loadRequestSeq) return
    plans.value = result?.records || result?.list || []
    const hasCurrentPlan = plans.value.some(plan => String(plan.id) === String(props.modelValue))
    if (!hasCurrentPlan) {
      emit('update:modelValue', plans.value[0]?.id || null)
    }
  } catch (error) {
    if (requestSeq !== loadRequestSeq) return
    console.error('Failed to load enterprise invoice plans', error)
    ElMessage.error('加载企营增方案列表失败')
  } finally {
    if (requestSeq === loadRequestSeq) {
      loading.value = false
    }
  }
}

onMounted(() => {
  loadPlans()
})

defineExpose({ loadPlans })
</script>
